/*
 * Convert Image to Chart — the tile grid's chart types.
 *
 * The approved sheet offers twelve tiles; the API's `ChartType` enum has only
 * seven members. The extra five are *variants* the enum expresses through
 * other fields, so one tile is a (chart_type, is_stacked, per-series render)
 * triple rather than a bare enum value:
 *
 *   Stacked Col / Stacked Bar / Stacked Area → base type + `is_stacked`
 *   Combo                                    → base type + per-series `render_as`
 *   Radar                                    → no enum member at all (see below)
 *
 * Radar is deliberately offered even though the backend cannot report it: the
 * model maps an exotic source chart to "the closest of these, with a warning"
 * (see ChartType's docstring), so a radar image comes back as `line` and the
 * user is the one who can say otherwise. Choosing it rebuilds a native radar
 * from the same categories/series — nothing about the extracted data changes.
 *
 * This module is the single place the tile set, its labels, and its mapping in
 * both directions live: `chartTypeOptionFor` picks the tile an extraction
 * pre-selects, and `applyChartTypeOption` re-shapes the extracted chart to
 * whatever tile the user ends on, for the preview and the insert alike.
 */

import type { ApiChartType, ChartSeries, EditableChart, SeriesRender } from "./convertChartApi";

/** Stable tile id. Used as the radio-group value, the telemetry suffix, and
 *  the preview renderer's switch key. */
export type ChartTypeOptionId =
  | "column"
  | "stackedColumn"
  | "bar"
  | "stackedBar"
  | "line"
  | "pie"
  | "donut"
  | "area"
  | "stackedArea"
  | "scatter"
  | "radar"
  | "combo";

/**
 * How a tile is drawn, independent of the API enum.
 *
 * `radar` and `combo` have no `ApiChartType` of their own, which is exactly
 * why the preview renderer and the PptxGenJS builder switch on this rather
 * than on `EditableChart.chartType`.
 */
export type ChartRenderKind = "column" | "bar" | "line" | "area" | "pie" | "doughnut" | "scatter" | "radar" | "combo";

export interface ChartTypeOption {
  readonly id: ChartTypeOptionId;
  /** Tile caption — matches the approved mockup wording exactly. */
  readonly label: string;
  /** How the preview and the inserted chart draw it. */
  readonly render: ChartRenderKind;
  /** The nearest `ChartType` on the wire, for `chartTypeOptionFor`'s reverse
   *  lookup. Radar has no member of its own and rides on `line`. */
  readonly apiChartType: ApiChartType;
  readonly stacked: boolean;
}

/** Named rather than inlined below so the fallbacks can reference it without an
 *  indexed read, which `noUncheckedIndexedAccess` would force an assertion
 *  around — and an assertion is exactly the wrong thing on a default value. */
const COLUMN_OPTION: ChartTypeOption = {
  id: "column",
  label: "Column",
  render: "column",
  apiChartType: "column",
  stacked: false,
};

/**
 * The twelve tiles, in the mockup's reading order (4 across, 3 down).
 *
 * Order is load-bearing: the grid renders this array as-is, so the sheet's
 * layout is changed here rather than in the component.
 */
export const CHART_TYPE_OPTIONS: readonly ChartTypeOption[] = [
  COLUMN_OPTION,
  { id: "stackedColumn", label: "Stacked Col", render: "column", apiChartType: "column", stacked: true },
  { id: "bar", label: "Bar", render: "bar", apiChartType: "bar", stacked: false },
  { id: "stackedBar", label: "Stacked Bar", render: "bar", apiChartType: "bar", stacked: true },
  { id: "line", label: "Line", render: "line", apiChartType: "line", stacked: false },
  { id: "pie", label: "Pie", render: "pie", apiChartType: "pie", stacked: false },
  { id: "donut", label: "Donut", render: "doughnut", apiChartType: "doughnut", stacked: false },
  { id: "area", label: "Area", render: "area", apiChartType: "area", stacked: false },
  { id: "stackedArea", label: "Stacked Area", render: "area", apiChartType: "area", stacked: true },
  { id: "scatter", label: "Scatter", render: "scatter", apiChartType: "scatter", stacked: false },
  { id: "radar", label: "Radar", render: "radar", apiChartType: "line", stacked: false },
  { id: "combo", label: "Combo", render: "combo", apiChartType: "column", stacked: false },
];

/** The tile selected when an extraction can't be matched to a better one. */
export const DEFAULT_CHART_TYPE_OPTION_ID: ChartTypeOptionId = "column";

/**
 * Look a tile up by id.
 *
 * `find` rather than an object index: the id reaches here from a DOM `value`
 * attribute, and a plain-object lookup would resolve `"constructor"` to an
 * inherited Object.prototype member and hand back a function typed as an
 * option (§3 of the coding standards).
 */
export function chartTypeOption(id: string): ChartTypeOption | null {
  return CHART_TYPE_OPTIONS.find((option) => option.id === id) ?? null;
}

/** The tile for an id, falling back to the default rather than throwing —
 *  every caller here is rendering, and a missing tile must not blank the sheet. */
export function chartTypeOptionOrDefault(id: string): ChartTypeOption {
  return chartTypeOption(id) ?? COLUMN_OPTION;
}

/**
 * True when the extraction describes a combo: at least one series drawn
 * differently from the chart's base type. `render_as` is `null` on a series
 * that simply follows the base, so only an explicit, *differing* value counts.
 */
export function isComboExtraction(chart: EditableChart): boolean {
  const base: SeriesRender | null =
    chart.chartType === "column" || chart.chartType === "bar" || chart.chartType === "line" || chart.chartType === "area"
      ? chart.chartType
      : null;
  if (base === null) return false;
  return chart.series.some((series) => series.renderAs !== null && series.renderAs !== base);
}

/**
 * The tile an extraction pre-selects — "AI read the image: <label>
 * pre-selected" in the sheet's banner.
 *
 * Combo wins over the base type, because a mixed chart rebuilt as a plain
 * column chart silently loses the distinction the model went to the trouble of
 * reporting.
 */
export function chartTypeOptionFor(chart: EditableChart): ChartTypeOption {
  if (isComboExtraction(chart)) return chartTypeOptionOrDefault("combo");

  const match = CHART_TYPE_OPTIONS.find(
    (option) => option.apiChartType === chart.chartType && option.stacked === chart.isStacked && option.id !== "radar",
  );
  return match ?? COLUMN_OPTION;
}

/**
 * Re-shape an extracted chart to the tile the user has selected.
 *
 * Only presentation fields move — categories, values, colours, titles and
 * scales are the extraction's answer and are never rewritten here. Two
 * corrections are applied because they would otherwise contradict the tile:
 *
 *  - Leaving `renderAs` set on a non-combo tile would make the builder emit a
 *    mixed chart the user just chose *not* to have, so it is cleared.
 *  - `secondary` axis refs only mean anything on a combo, and a single-type
 *    chart with a stray secondary series plots on an axis PowerPoint won't
 *    draw — so those collapse back to primary too.
 */
export function applyChartTypeOption(chart: EditableChart, option: ChartTypeOption): EditableChart {
  const keepSeriesShape = option.render === "combo";
  const series: ChartSeries[] = keepSeriesShape
    ? [...chart.series]
    : chart.series.map((entry) => ({ ...entry, renderAs: null, axis: "primary" as const }));

  // The extracted axis scale describes the SOURCE chart's plot. Stacking
  // changes what the axis has to span — four clustered bars peaking at 320 all
  // fit under a 350 max, but stacked they reach 630 and everything above 350 is
  // clipped off the top of the plot. The reverse is just as wrong, leaving a
  // stacked chart's tall axis over short unstacked bars. So the moment the
  // user's tile disagrees with how the source was drawn, the source's scale
  // stops applying and PowerPoint re-fits it.
  const stackingChanged = option.stacked !== chart.isStacked;

  return {
    ...chart,
    chartType: option.apiChartType,
    isStacked: option.stacked,
    series,
    yAxis: stackingChanged ? null : chart.yAxis,
    // A stacked chart's secondary axis title describes an axis that no longer
    // exists once the series collapse onto one scale.
    secondaryYAxisTitle: keepSeriesShape ? chart.secondaryYAxisTitle : null,
    secondaryYAxis: keepSeriesShape && !stackingChanged ? chart.secondaryYAxis : null,
  };
}
